import { initAndGetAnalyticsSdk, initAdobeDataLayerMMT } from '@mmt/event-logger';
import { LOCUS_API_ENDPOINT } from './constants';

export let AnalyticsSdk: ReturnType<typeof initAndGetAnalyticsSdk> | null = null;

export function initAnalytics() {
  if (typeof window === 'undefined') return;

  try {
    AnalyticsSdk = initAndGetAnalyticsSdk?.({ org: 'MMT' }) || null;

    const isDev = process.env.NODE_ENV !== 'production';
    const scriptHtml = initAdobeDataLayerMMT?.(isDev ? 'development' : 'production');
    if (!scriptHtml) return;

    const tempDiv = document?.createElement('div');
    if (!tempDiv) return;
    tempDiv.innerHTML = scriptHtml;
    const scriptElement = tempDiv?.querySelector('script');

    if (scriptElement?.src && !document?.querySelector(`script[src="${scriptElement.src}"]`)) {
      const newScript = document?.createElement('script');
      if (newScript) {
        newScript.src = scriptElement.src;
        newScript.async = true;
        document?.head?.appendChild(newScript);
      }
    }
  } catch (e) {
    console.error('Analytics: initialization failed', e);
  }
}

export interface LocusData {
  fromLocusCode: string;
  toLocusCode: string;
  fromLobCode: string;
  toLobCode: string;
  fromStationName: string;
  toStationName: string;
  sourceLocusType: string;
  destinationLocusType: string;
  sourceLocusV2Id: string;
  sourceLocusV2Type: string;
  destinationLocusV2Id: string;
  destinationLocusV2Type: string;
}

export async function fetchLocusData(fromStationCode?: string, toStationCode?: string): Promise<LocusData | null> {
  try {
    if (typeof window === 'undefined') return null;
    const from = fromStationCode || '';
    const to = toStationCode || '';
    if (!from && !to) return null;

    const url = `${LOCUS_API_ENDPOINT}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const res = await fetch(url);
    if (!res?.ok) return null;
    const json = await res?.json();
    if (!json || json?.error || !json?.data) return null;
    return json.data;
  } catch {
    return null;
  }
}

export const buildSearchContext = (opts?: {
  fromCode?: string;
  fromName?: string;
  toCode?: string;
  toName?: string;
  locusData?: LocusData | null;
}) => {
  const { fromCode = 'NA', fromName = 'NA', toCode = 'NA', toName = 'NA', locusData } = opts || {};

  return {
    from: {
      locus: {
        locus_id: locusData?.fromLocusCode || 'NA',
        locus_type: locusData?.sourceLocusType || 'NA',
        country: 'India',
        locus_v2_id: locusData?.sourceLocusV2Id || 'NA',
        locus_v2_type: locusData?.sourceLocusV2Type || 'NA'
      },
      location: { code: fromCode, name: fromName, country_code: 'IN', country: 'India' }
    },
    to: {
      locus: {
        locus_id: locusData?.toLocusCode || 'NA',
        locus_type: locusData?.destinationLocusType || 'NA',
        country: 'India',
        locus_v2_id: locusData?.destinationLocusV2Id || 'NA',
        locus_v2_type: locusData?.destinationLocusV2Type || 'NA'
      },
      location: { code: toCode, name: toName, country_code: 'IN', country: 'India' }
    },
    travel_class: 'All',
    advance_purchase: 'NA'
  };
};

export const generateUUID = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const getTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'Asia/Kolkata';
  }
};

export const getSessionID = (): string => {
  const SESSION_KEY = 'rails_seo_session_id';
  if (typeof window === 'undefined') return generateUUID();

  try {
    let sessionId = window?.sessionStorage?.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = generateUUID();
      window?.sessionStorage?.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return generateUUID();
  }
};
